import React from 'react';
import {
	Badge,
	Link,
	ListItemIcon,
	ListItemText,
	ListItem,
} from '@mui/material';
import classNames from 'classnames';

import { useBlockingCallback } from '../../../hooks/useBlockingCallback';
import styles from './CustomListItem.module.scss';
import { useNewLeadsSubscription } from '../../../hooks/useNewLeadsSubscription';

export type BaseListItemType = {
	id: string;
	label?: string;
	icon?: React.JSX.Element;
	isVisible?: boolean;
	isDisabled?: boolean;
	isActive?: boolean;
	className?: string;
	ref?: React.RefObject<HTMLLIElement>;
};

export type CustomMenuLinkItemType = BaseListItemType & {
	href: string;
} & Partial<Pick<HTMLAnchorElement, 'target' | 'rel'>>;

export type CustomMenuButtonItemType = BaseListItemType & {
	onClick?: ( event?: React.MouseEvent<HTMLElement> ) => void;
};

export type CustomListItemType =
	| CustomMenuLinkItemType
	| CustomMenuButtonItemType;

const LinkWrap = ( {
	href,
	target,
	rel,
	children,
}: Partial<Pick<CustomMenuLinkItemType, 'href' | 'target' | 'rel'>> & {
	children: React.JSX.Element;
} ) => {
	return href ? (
		<Link href={ href } target={ target } rel={ rel } className={ styles.linkWrap }>
			{ children }
		</Link>
	) : (
		children
	);
};

export const CustomListItemComponent: React.FC<{
	listItem: CustomListItemType;
}> = ( {
	listItem: {
		isDisabled,
		isActive,
		label,
		icon,
		isVisible,
		id,
		className,
		ref,
		...rest
	},
}: {
	listItem: CustomListItemType;
} ) => {
	const { badgeContent } = useNewLeadsSubscription( id !== 'inquiries' );

	const blockedOnClick = useBlockingCallback(
		'onClick' in rest ? rest.onClick : undefined
	);

	if ( isVisible !== undefined && !isVisible ) return null;
	if ( isDisabled !== undefined && isDisabled ) return null;

	return (
		<LinkWrap
			href={ 'href' in rest ? rest.href : undefined }
			target={ 'target' in rest ? rest.target : undefined }
			rel={ 'rel' in rest ? rest.rel : undefined }
		>
			<ListItem
				data-cy={ `${ id }ListItem` }
				onClick={ blockedOnClick }
				className={ classNames( styles.menuItem, className, {
					[ styles.menuItemActive ]: isActive,
				} ) }
				ref={ ref }
			>
				<Badge
					badgeContent={ badgeContent }
					color='primary'
					className={ styles.badge }
					anchorOrigin={ { vertical: 'top', horizontal: 'left' } }
				>
					{ icon && <ListItemIcon>{ icon }</ListItemIcon> }
					<ListItemText>{ label }</ListItemText>
				</Badge>
			</ListItem>
		</LinkWrap>
	);
};
